<template>
    <div class="shop-filters">
        <div class="shop-filter-top">
            <div class="close-shop-filter">
                <i class="bi bi-arrow-left"></i>
                <p class="mb-0 ml-3">{{ $trans('main.Filters') }}</p>
            </div>

            <button type="button" class="btn p-0 text-sm shop-filter-clear" @click="clearAllFilters">
                {{ $trans('main.Clear Filter') }}
            </button>
        </div>

        <FilterAccordion
            panel-id="filter-q"
            :title="$trans('main.Search')"
            :expanded="true"
            :is-active="Boolean(q)"
        >
            <div class="shop-filter-field">
                <input
                    type="text"
                    autocomplete="off"
                    v-model="q"
                    @keyup="filterWithQ"
                    :placeholder="$trans('main.Search')"
                    class="shop-filter-input"
                >
            </div>
        </FilterAccordion>

        <FilterAccordion
            panel-id="filter-category"
            :title="$trans('main.Category')"
            :expanded="true"
            :is-active="Boolean(selectedMainTypeId || selectedCategoryId || selectedSubCategoryId)"
        >
            <div class="shop-filter-select">
                <label class="shop-filter-label">{{ $trans('main.Main types') }}</label>
                <el-select
                    v-model="selectedMainTypeId"
                    :placeholder="$trans('main.Select an option')"
                    clearable
                    size="medium"
                    class="shop-filter-el-select"
                >
                    <el-option
                        v-for="mainType in mainTypes"
                        :key="mainType.id"
                        :label="mainType.name"
                        :value="mainType.id"
                    />
                </el-select>
            </div>

            <div class="shop-filter-select">
                <label class="shop-filter-label">{{ $trans('main.Category') }}</label>
                <el-select
                    v-model="selectedCategoryId"
                    :placeholder="$trans('main.Select an option')"
                    clearable
                    size="medium"
                    class="shop-filter-el-select"
                >
                    <el-option
                        v-for="category in filteredCategories"
                        :key="category.id"
                        :label="category.current_ml.name"
                        :value="category.id"
                    />
                </el-select>
            </div>

            <div v-if="filteredSubCategories.length" class="shop-filter-select">
                <label class="shop-filter-label">{{ $trans('main2.Choose a subcategory') }}</label>
                <el-select
                    v-model="selectedSubCategoryId"
                    :placeholder="$trans('main2.Choose a subcategory')"
                    clearable
                    size="medium"
                    class="shop-filter-el-select"
                >
                    <el-option
                        v-for="subCategory in filteredSubCategories"
                        :key="subCategory.id"
                        :label="subCategory.current_ml.name"
                        :value="subCategory.id"
                    />
                </el-select>
            </div>

            <p
                v-if="selectedCategoryId && !selectedSubCategoryId && filteredSubCategories.length"
                class="shop-filter-hint"
            >
                {{ $trans('main2.Choose a subcategory') }}
            </p>
        </FilterAccordion>

        <FormFieldFilterAccordion
            v-for="formField in formFields"
            :key="formField.id"
            :formField="formField"
            :onChangeHandler="onChangeHandler"
            :is-active="isFormFieldActive(formField)"
        />

        <PriceRangeFilterAccordion
            v-for="priceFilter in priceFilters"
            :key="'price-' + priceFilter.id"
            :price-filter="priceFilter"
            :onChangeHandler="onChangeHandler"
        />
    </div>
</template>

<script>
import FilterAccordion from './FilterAccordion.vue';
import FormFieldFilterAccordion from './FormFields/FormFieldFilterAccordion.vue';
import PriceRangeFilterAccordion from './PriceRangeFilterAccordion.vue';

export default {
    name: 'Filters',
    components: {FilterAccordion, FormFieldFilterAccordion, PriceRangeFilterAccordion},
    props: {
        mainTypes: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
        subCategories: {
            type: Array,
            required: true,
        },
        fetchProducts: {
            type: Function,
            required: true,
        },
        formFields: {
            type: Array,
            required: true,
        },
        priceFilters: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            selectedCategoryId: null,
            selectedSubCategoryId: null,
            selectedMainTypeId: null,
            query: '',
            timeOut: null,
        }
    },
    computed: {
        selectedFilters() {
            try {
                const search = location.search.substring(1);

                return JSON.parse('{"' + decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}')
            } catch (e) {
                return {};
            }
        },
        filteredCategories() {
            if (!this.selectedMainTypeId) {
                return this.categories;
            }

            const mainType = this.mainTypes.find(mainType => mainType.id === this.selectedMainTypeId);

            return mainType?.categories ?? [];
        },
        filteredSubCategories() {
            if (!this.selectedCategoryId) {
                return [];
            }

            const category = this.categories.find(category => category.id === this.selectedCategoryId)

            return category?.sub_categories ?? [];
        },
        q: {
            get() {
                if (this.query) {
                    return this.query
                }

                if (this.selectedFilters?.q) {
                    return this.selectedFilters.q
                }

                return '';
            },
            set(val) {
                this.query = val;
                return this.selectedFilters.q = val;
            }
        }
    },

    watch: {
        selectedMainTypeId(val, oldVal) {
            if (val === oldVal) {
                return;
            }

            if (!val) {
                this.clearCategoryFilterValues();
                this.selectedCategoryId = null;
                this.selectedSubCategoryId = null;
                this.onChangeHandler('main_type_id', null, false);
                this.onChangeHandler('category_id', null, false);
                this.onChangeHandler('sub_category_id', null, true);
                return;
            }

            if (oldVal || !this.selectedFilters?.main_type_id) {
                this.clearCategoryFilterValues();
                if (!this.selectedFilters?.category_id) {
                    this.selectedCategoryId = null;
                    this.selectedSubCategoryId = null;
                    this.onChangeHandler('category_id', null, false)
                }
                this.onChangeHandler('main_type_id', val, true)
            }
        },
        selectedCategoryId(val, oldVal) {
            if (val === oldVal) {
                return;
            }

            if (!val) {
                this.clearCategoryFilterValues();
                this.selectedSubCategoryId = null;
                this.onChangeHandler('sub_category_id', null, false);
                this.onChangeHandler('category_id', null, true);
                return;
            }

            if (oldVal || !this.selectedFilters?.category_id) {
                this.clearCategoryFilterValues();
                this.selectedSubCategoryId = null;
                this.onChangeHandler('sub_category_id', null, false)
                this.onChangeHandler('category_id', val, true)
            }
        },
        selectedSubCategoryId(val, oldVal) {
            if (val === oldVal) {
                return;
            }

            this.clearCategoryFilterValues();

            if (!val) {
                this.onChangeHandler('sub_category_id', null, true);
                return;
            }

            this.onChangeHandler('sub_category_id', val, true)
        }
    },

    methods: {
        isFormFieldActive(formField) {
            const slug = formField.slug;
            const filters = this.selectedFilters;

            return Boolean(
                filters[`additional_data[${slug}]`]
                || filters[`additional_data[${slug}_option_id]`]
                || (filters[`additional_data_min[${slug}]`] !== undefined && filters[`additional_data_min[${slug}]`] !== '')
                || (filters[`additional_data_max[${slug}]`] !== undefined && filters[`additional_data_max[${slug}]`] !== '')
            );
        },
        clearAdditionalDataFilters() {
            const selectedFilters = this.selectedFilters;

            Object.keys(selectedFilters).forEach((key) => {
                if (key.startsWith('additional_data[')) {
                    delete selectedFilters[key];
                }
            });
        },
        clearPriceFilters() {
            const selectedFilters = this.selectedFilters;

            delete selectedFilters.min_price;
            delete selectedFilters.max_price;

            Object.keys(selectedFilters).forEach((key) => {
                if (key.startsWith('additional_data_min[') || key.startsWith('additional_data_max[')) {
                    delete selectedFilters[key];
                }
            });
        },
        clearCategoryFilterValues() {
            this.clearAdditionalDataFilters();
            this.clearPriceFilters();
        },
        clearAllFilters() {
            const lang = document.querySelector('html').getAttribute('lang');
            history.pushState({}, null, `/${lang}/products`);
            this.selectedMainTypeId = null;
            this.selectedCategoryId = null;
            this.selectedSubCategoryId = null;
            this.query = '';
            this.fetchProducts();
        },
        setSelectedIds(selectedFilters) {
            this.mainTypes.forEach(mainType => {
                mainType.categories.forEach(category => {
                    if (category.id === +selectedFilters.category_id) {
                        this.selectedCategoryId = category.id;
                        this.selectedMainTypeId = mainType.id;
                        return 0;
                    }

                    category.sub_categories.forEach(subCategory => {
                        if (subCategory.id === +selectedFilters.category_id) {
                            this.selectedCategoryId = category.id;
                            this.selectedSubCategoryId = subCategory.id;
                            this.selectedMainTypeId = mainType.id;
                            return 0;
                        }
                    })
                })
            })
        },
        onChangeHandler(key, value, needRequest = true) {
            const selectedFilters = this.selectedFilters;

            if (!value && value !== 0) {
                try {
                    delete selectedFilters[key];
                } catch (e) {}
            } else {
                selectedFilters[key] = value;
            }
            const queryString = new URLSearchParams(selectedFilters).toString();
            let url = '/' + document.querySelector('html').getAttribute('lang');
            url = url + '/products?' + queryString;

            history.pushState({}, null, url)

            if (needRequest) {
                this.fetchProducts()
            }
        },
        filterWithQ() {
            clearTimeout(this.timeOut);
            this.timeOut = setTimeout(() => {
                this.onChangeHandler('q', this.query, true)
            }, 500)
        }
    },

    updated() {
        if (!this.selectedMainTypeId) {
            const selectedFilters = this.selectedFilters;

            if (selectedFilters?.category_id) {
                this.setSelectedIds(selectedFilters);
            }
        }
    },
    mounted() {
        this.stripLegacySentinelPrices();

        const selectedFilters = this.selectedFilters;
        if (selectedFilters?.main_type_id) {
            this.selectedMainTypeId = +selectedFilters.main_type_id;
        }

        if (selectedFilters?.category_id) {
            this.selectedCategoryId = +selectedFilters.category_id;
        }

        if (selectedFilters?.sub_category_id) {
            this.selectedSubCategoryId = +selectedFilters.sub_category_id;
        }
    }
}
</script>
